/**
 * OrderPrintDocument — the one thing on paper when a CSR prints an order.
 *
 * Rendered through PrintDocumentRoot, so while printing the alert bar, the
 * email pane and the interactive order panel (inputs, buttons, tabs) are
 * hidden and only this document prints. Everything here is static, so it
 * reports ready on the next frame.
 */
import { useEffect } from 'react';
import type { OrderPrintJob } from '../../utils/orderPrintModel';
import PrintDocumentRoot from './PrintDocumentRoot';
import PrintLineItemsTable from './PrintLineItemsTable';
import styles from './OrderPrintDocument.module.css';

interface OrderPrintDocumentProps {
  job: OrderPrintJob;
  onReady: () => void;
}

const OrderPrintDocument: React.FC<OrderPrintDocumentProps> = ({ job, onReady }) => {
  useEffect(() => {
    // One frame so the document is laid out before the print snapshot.
    const raf = requestAnimationFrame(onReady);
    return () => cancelAnimationFrame(raf);
  }, [onReady]);

  const { order } = job;
  const subtitle = [job.fields.find((f) => f.label === 'Status')?.value, order.sage_order_no, order.po_number && `PO ${order.po_number}`]
    .filter(Boolean)
    .join(' · ');

  return (
    <PrintDocumentRoot
      title={`${order.customer_name} (${order.customer_no})`}
      subtitle={subtitle}
      testId="order-print-document"
    >
      <dl className={styles.fields}>
        {job.fields.map((f) => (
          <div key={f.label} className={styles.field}>
            <dt>{f.label}</dt>
            <dd>{f.value}</dd>
          </div>
        ))}
      </dl>

      {job.customerOrderNotes && (
        <section className={styles.section} data-testid="order-print-customer-notes">
          <h2 className={styles.heading}>Customer notes (from Sage)</h2>
          <div className={styles.prose}>{job.customerOrderNotes}</div>
        </section>
      )}

      <section className={styles.section}>
        <h2 className={styles.heading}>Line items ({job.lineItems.length})</h2>
        <PrintLineItemsTable lineItems={job.lineItems} />
      </section>

      {job.instructions.length > 0 && (
        <section className={styles.section} data-testid="order-print-instructions">
          <h2 className={styles.heading}>Order instructions (Sage /C lines)</h2>
          <ol className={styles.list}>
            {job.instructions.map((text, i) => (
              <li key={i}>{text}</li>
            ))}
          </ol>
        </section>
      )}

      {job.sageNotes && (job.sageNotes.orderNotes || job.sageNotes.csNotes.length > 0) && (
        <section className={styles.section} data-testid="order-print-sage-notes">
          <h2 className={styles.heading}>Order notes</h2>
          {job.sageNotes.orderNotes && <div className={styles.prose}>{job.sageNotes.orderNotes}</div>}
          {job.sageNotes.csNotes.length > 0 && (
            <ul className={styles.list}>
              {job.sageNotes.csNotes.map((text, i) => (
                <li key={i}>{text}</li>
              ))}
            </ul>
          )}
        </section>
      )}
    </PrintDocumentRoot>
  );
};

export default OrderPrintDocument;
